import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import User from "../../../api/auth.js";
import AuthLayout from "../../../components/organisms/Layout/Authlayout";
import { heyhireBlackIcon } from "../../../assets/images.js";
import { toast } from "react-hot-toast";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

const SuccessView = ({ resend }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <i className="fas fa-check text-green-600 text-xl"></i>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t("login.password-reset-email")}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t("login.you-do-not-received-email")}&nbsp;
          <button
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            onClick={resend}
          >
            {t("login.resend")}
          </button>
        </p>
      </div>
    </div>
  );
};

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [emailNotRegistered, setEmailNotRegistered] = useState(false);

  useEffect(() => {
    document.title = t("title.heyhire-portal-forgot-password");
    return () => {
      setEmail("");
      setLoading(false);
      setValidationErrors(null);
    };
  }, []);

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;
    setInvalidEmail(false);
    setEmailNotRegistered(false);
    if (name === "email") {
      setEmail(value);
    }
  };

  const validEmail = (email) => {
    const re =
      /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
    const checkEmail = email.match(re);
    return checkEmail;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    if (validEmail(email)) {
      const body = {
        email: email,
      };
      User.ResetPasswordRequest(body)
        .then(() => {
          setLoading(false);
          localStorage.setItem("passwordResetEmail", email);
          toast.success(t("login.password-reset-email"));
        })
        .catch((error) => {
          setLoading(false);
          if (
            error?.response?.data?.message ===
            'Attempt to read property "id" on null'
          ) {
            setEmailNotRegistered(true);
          } else {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        });
    } else {
      setLoading(false);
      setInvalidEmail(true);
    }
  };

  return (
    <AuthLayout
      showLink={!completed}
      title={
        completed ? t("login.thanks-reset-password") : t("login.reset-password")
      }
    >
      <div className="flex min-h-screen flex-1 flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {!completed ? (
            <>
              {/* Logo and Title */}
              <div className="text-center mb-8">
                <img
                  className="mx-auto h-12 w-auto"
                  src={heyhireBlackIcon}
                  alt="HeyHire"
                />
                <h2 className="mt-8 text-center text-3xl font-bold tracking-tight text-gray-900">
                  {t("login.reset-password")}
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                  {t("login.enter-email-reset")}
                </p>
              </div>

              {/* Form Card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
                <form className="space-y-6" onSubmit={handleLogin}>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {t("common.email")}
                    </label>
                    <div className="relative">
                      <input
                        className={`block w-full rounded-lg border ${invalidEmail || emailNotRegistered || validationErrors?.email
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                          } px-4 py-2.5 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 sm:text-sm transition-colors`}
                        name="email"
                        type="email"
                        value={email}
                        placeholder={t("login.email-input-placeholder")}
                        onChange={handleInputChange}
                      />
                      {(invalidEmail || emailNotRegistered || validationErrors?.email) && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ExclamationCircleIcon
                            aria-hidden="true"
                            className="h-5 w-5 text-red-500"
                          />
                        </div>
                      )}
                    </div>
                    {invalidEmail && email && (
                      <p className="mt-2 text-sm text-red-600">
                        {t("errors.invalid-email")}
                      </p>
                    )}
                    {invalidEmail && !email && (
                      <p className="mt-2 text-sm text-red-600">
                        {t("errors.email-required")}
                      </p>
                    )}
                    {emailNotRegistered && (
                      <p className="mt-2 text-sm text-red-600">
                        {t("errors.email-not-registered")}
                      </p>
                    )}
                    {validationErrors?.email && (
                      <p className="mt-2 text-sm text-red-600">
                        {validationErrors.email[0]}
                      </p>
                    )}
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex w-full justify-center items-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <i className="fad fa-circle-notch fa-spin mr-2" />
                          {t("common.continue")}...
                        </>
                      ) : (
                        t("login.continue")
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Links */}
              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
                >
                  {t("login.return-signin")}
                </Link>
              </div>
            </>
          ) : (
            <SuccessView resend={() => setCompleted(false)} />
          )}
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
